import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import APIMeSub from "./me/client";
import APIConfirmEmailSub from "./confirmEmail/client";
import APIRecoverySub from "./recovery/client";
import APISendRecoverySub from "./sendRecovery/client";
import APIUserIdSub from "./userId/client";
import * as types from "@/app/types";

export default class APIUsers extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  me = new APIMeSub(this._client);
  confirmEmail = new APIConfirmEmailSub(this._client);
  recovery = new APIRecoverySub(this._client);
  sendRecovery = new APISendRecoverySub(this._client);
  userId = new APIUserIdSub(this._client);

  getMe = async (): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/me`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putMe = async ({
    ...body
  }: types.UserUpdate): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/me`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateMyAIContext = async (
    ai_context: string | null,
  ): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/me/ai-context`,
      method: "PATCH",
      body: { ai_context },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getContacts = async (): Promise<types.UserContact[]> => {
    let res = await this._fetch({
      url: `/users/contacts`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getVapidPublicKey = async (): Promise<string> => {
    let res = await this._fetch({
      url: `/users/vapid_public_key`,
      method: "GET",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postFollow = async ({ ...body }: types.UserFollow): Promise<any> => {
    let res = await this._fetch({
      url: `/users/follow`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postUnfollow = async ({ ...body }: types.UserFollow): Promise<any> => {
    let res = await this._fetch({
      url: `/users/unfollow`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postIgnoreContact = async ({
    ...body
  }: types.UserFollow): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/ignore_contact`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postDeleteContact = async ({ ...body }: types.UserFollow): Promise<any> => {
    let res = await this._fetch({
      url: `/users/delete_contact`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postSubscriptionInfo = async ({
    ...body
  }: object): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/subscription_info`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postReqPhoneVerif = async (): Promise<any> => {
    let res = await this._fetch({
      url: `/users/req-phone-verif`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postVerifyNumber = async ({
    code,
  }: {
    code: string;
  }): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/verify-number`,
      method: "POST",
      params: { code: code },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postResendVerification = async (): Promise<any> => {
    let res = await this._fetch({
      url: `/users/resend-verification`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postChangeEmail = async ({
    ...body
  }: types.ChangeEmailRequest): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/change-email`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postChangePassword = async ({
    ...body
  }: types.PasswordChangeRequest): Promise<types.Token> => {
    let res = await this._fetch({
      url: `/users/change-password`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getMyProducts = async (): Promise<types.ProductResponse[]> => {
    let res = await this._fetch({
      url: `/users/products`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getUserProducts = async (userId: UUID): Promise<types.ProductResponse[]> => {
    let res = await this._fetch({
      url: `/users/${userId}/products`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getBatch = async ({
    ids,
  }: {
    ids: string;
  }): Promise<types.UserResponse[]> => {
    let res = await this._fetch({
      url: `/users/batch`,
      method: "GET",
      params: { ids: ids },
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getIdOrCellphone = async (
    idOrCellphone: string,
  ): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/users/${idOrCellphone}`,
      method: "GET",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
